import { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink } from 'react-router';
import api, { BROWSER_TZ } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { PageBanner } from '@/components/PageBanner';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { RegionalMapView } from '@/components/RegionalMapView';
import { DetailTableRangeTabs, TrendRangeTabs } from '@/components/TrendRangeTabs';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { EmptyState } from '@/components/EmptyState';
import { getTopicIconComponent } from '@/lib/topicIcons';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
  formatTrendTick,
  formatTrendTooltipLabel,
  getTrendXAxisInterval,
} from '@/lib/chartTheme';
import { 
  TrendingUp, TrendingDown, Minus, MessageSquare,
  Globe, Flame, Zap, Sparkles,
  AlertTriangle, CheckCircle, FileQuestion, ThumbsUp,
  Activity, HeartPulse, ListChecks
} from 'lucide-react';
import {
  CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend, Line, LineChart,
} from 'recharts';

const TOPIC_SERIES_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

function getTopicName(topic) {
  if (typeof topic === 'string') return topic;
  return topic?.topic_name || topic?.topic || topic?.name || topic?.title || topic?.topic_id || null;
}

function getRegionalTopicCount(region) {
  if (region?.topic_count != null) return Number(region.topic_count) || 0;
  if (region?.topics_count != null) return Number(region.topics_count) || 0;
  if (region?.unique_topics != null) return Number(region.unique_topics) || 0;

  const topics = Array.isArray(region?.topics)
    ? region.topics
    : (Array.isArray(region?.top_topics) ? region.top_topics : []);

  return new Set(topics.map(getTopicName).filter(Boolean)).size;
}

function getRegionalQueryCount(region) {
  if (region?.query_count != null) return Number(region.query_count) || 0;
  if (region?.volume != null) return Number(region.volume) || 0;
  if (region?.query_volume != null) return Number(region.query_volume) || 0;
  return 0;
}

const TREND_COLORS = {
  hot: { text: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30' },
  warm: { text: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30' },
  stable: { text: 'text-muted-foreground', bg: 'bg-secondary', border: 'border-border' },
  cooling: { text: 'text-info', bg: 'bg-info/10', border: 'border-info/30' },
};

// Health score is a rated 0-100 value, so both the number and its meter must be
// driven from the fixed semantic tokens, never `bg-primary` — index.css remaps
// --primary to the tenant's brand color, which made every health bar render in
// the brand hue regardless of score (EIP-316), e.g. a green "95" over a red bar.
const HEALTH_STATES = {
  good: { text: 'text-success', bar: 'bg-success' },
  fair: { text: 'text-warning', bar: 'bg-warning' },
  poor: { text: 'text-destructive', bar: 'bg-destructive' },
};

// Non-finite scores fall through to `poor`, matching the previous getHealthColor.
function getHealthState(score) {
  if (score >= 70) return HEALTH_STATES.good;
  if (score >= 40) return HEALTH_STATES.fair;
  return HEALTH_STATES.poor;
}

function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendingUp className="w-4 h-4 text-success" />;
  if (trend === 'down') return <TrendingDown className="w-4 h-4 text-destructive" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

function HeatBadge({ heat }) {
  // Only show badge if there's actual heat data (not stable/default)
  if (!heat || heat === 'stable') return null;
  
  const colors = TREND_COLORS[heat] || TREND_COLORS.stable;
  const icons = { hot: Flame, warm: Zap, cooling: TrendingDown };
  const Icon = icons[heat] || null;
  
  if (!Icon) return null;
  
  return (
    <Badge variant="outline" className={`${colors.bg} ${colors.text} ${colors.border} text-xs`}>
      <Icon className="w-3 h-3 mr-1" />
      {heat.charAt(0).toUpperCase() + heat.slice(1)}
    </Badge>
  );
}

function TopicRow({ topic, rank }) {
  const colors = TREND_COLORS[topic.heat] || TREND_COLORS.stable;
  const hasChangeData = topic.change_pct !== 0;
  const TopicIcon = getTopicIconComponent(topic.icon);
  
  return (
    <div className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl ${colors.bg} transition-all hover:shadow-sm`}>
      <div
        className="relative w-8 h-8 rounded-xl flex items-center justify-center text-muted-foreground bg-card flex-shrink-0"
        style={{ color: topic.color || undefined }}
      >
        <TopicIcon className="h-4 w-4" aria-hidden="true" />
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-background px-1 text-[10px] font-bold text-muted-foreground shadow-sm">
          {rank}
        </span>
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground truncate">{topic.topic}</span>
          <HeatBadge heat={topic.heat} />
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {topic.document_count} docs in knowledge base
        </div>
      </div>
      
      <div className="text-right flex-shrink-0">
        <div className="font-bold text-foreground text-lg">{topic.volume}</div>
        {hasChangeData && (
          <div className="flex items-center gap-1 text-xs justify-end">
            <TrendIcon trend={topic.trend} />
            <span className={topic.change_pct >= 0 ? 'text-success' : 'text-destructive'}>
              {topic.change_pct >= 0 ? '+' : ''}{topic.change_pct}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TopicTable({ topics }) {
  if (!topics || topics.length === 0) {
    return (
      <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-8 text-center">
        <MessageSquare className="mb-3 h-9 w-9 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">No topic activity yet</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">Topics will appear once employee conversations are classified.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[680px] data-table">
        <thead>
          <tr className="border-b border-border">
            <th className="py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Topic</th>
            <th className="py-3 pr-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Volume</th>
            <th className="py-3 pr-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trend</th>
            <th className="py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Docs</th>
          </tr>
        </thead>
        <tbody>
          {topics.slice(0, 5).map((topic) => {
            const TopicIcon = getTopicIconComponent(topic.icon);
            return (
              <tr key={topic.topic_id || topic.topic} className="border-b border-border/50 hover:bg-secondary/30">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <span
                      className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-muted-foreground"
                      style={{ color: topic.color || undefined }}
                    >
                      <TopicIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-card px-1 text-[10px] font-bold shadow-sm">
                        {topic.rank}
                      </span>
                    </span>
                    <div>
                      <p className="font-medium text-foreground">{topic.topic}</p>
                      <HeatBadge heat={topic.heat} />
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 text-center font-medium text-foreground">{topic.volume}</td>
                <td className="py-3 pr-4 text-center">
                  <span className="inline-flex items-center justify-center gap-1">
                    <TrendIcon trend={topic.trend} />
                    {topic.change_pct !== 0 ? `${topic.change_pct >= 0 ? '+' : ''}${topic.change_pct}%` : '—'}
                  </span>
                </td>
                <td className="py-3 text-center text-muted-foreground">{topic.document_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EmergingTopicCard({ topic }) {
  return (
    <div className="p-4 bg-gradient-to-br from-primary/5 to-card rounded-xl border border-primary/20">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-medium text-foreground">{topic.topic}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{topic.parent}</p>
        </div>
        <Badge className="bg-primary/10 text-primary">
          {topic.growth}
        </Badge>
      </div>
      <div className="mt-3 text-sm text-muted-foreground">
        {topic.mentions} mentions
      </div>
    </div>
  );
}

// Topic Health Dashboard Component
function TopicHealthDashboard({ healthData, loading, window, onWindowChange }) {
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const { topics = [] } = healthData || {};

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-destructive/10 text-destructive',
      medium: 'bg-warning/10 text-warning',
      low: 'bg-success/10 text-success',
    };
    return (
      <Badge className={`${styles[priority] || styles.low} text-xs`}>
        {priority === 'high' && <AlertTriangle className="w-3 h-3 mr-1" />}
        {priority === 'medium' && <Activity className="w-3 h-3 mr-1" />}
        {priority === 'low' && <CheckCircle className="w-3 h-3 mr-1" />}
        {priority}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Topic Health Table */}
      <Card>
        <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <HeartPulse className="w-5 h-5 text-primary" />
              Topic Health by Priority
            </CardTitle>
            <CardDescription>
              Topics ranked by content team attention needed
            </CardDescription>
          </div>
          <DetailTableRangeTabs value={window} onChange={onWindowChange} className="w-full lg:w-auto lg:min-w-[360px]" />
        </CardHeader>
        <CardContent>
          {(!topics || topics.length === 0) ? (
            <EmptyState
              icon={HeartPulse}
              title="No topic health data available"
              description="Health metrics will appear once conversations are classified."
              variant="plain"
              compact
            />
          ) : (
            <div className="space-y-3">
              {topics.map((topic) => {
                const TopicIcon = getTopicIconComponent(topic.icon);
                const health = getHealthState(topic.health_score);
                return (
                  <div 
                    key={topic.topic_id} 
                    className={`p-4 rounded-xl border transition-all hover:shadow-sm ${
                      topic.priority === 'high' ? 'bg-destructive/5 border-destructive/30' :
                      topic.priority === 'medium' ? 'bg-warning/5 border-warning/30' :
                      'bg-muted/50 dark:bg-card/50 border-border dark:border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span
                          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground shadow-sm"
                          style={{ color: topic.color || undefined }}
                        >
                          <TopicIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-foreground">{topic.topic_name}</span>
                            {getPriorityBadge(topic.priority)}
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <FileQuestion className="w-3.5 h-3.5" />
                              {topic.content_gaps} gaps
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3.5 h-3.5" />
                              {topic.query_volume} queries
                            </span>
                            <span className="flex items-center gap-1">
                              {topic.document_count} docs
                            </span>
                            {topic.helpful_rate !== null && (
                              <span className="flex items-center gap-1">
                                <ThumbsUp className="w-3.5 h-3.5" />
                                {topic.helpful_rate}% helpful
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <div className={`text-lg font-bold ${health.text}`}>
                          {topic.health_score}
                        </div>
                        <div className="text-xs text-muted-foreground">health score</div>
                        <Progress
                          value={topic.health_score}
                          aria-label={`${topic.topic_name} health score`}
                          className="w-20 h-1.5 mt-1 bg-border"
                          indicatorClassName={health.bar}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TopicHealthTrendChart({ rows, window, topics, selectedTopicId, onTopicChange, onWindowChange, loading = false }) {
  const selectedTopic = useMemo(
    () => (topics || []).find((topic) => String(topic.topic_id) === String(selectedTopicId)),
    [selectedTopicId, topics]
  );
  const chartRows = useMemo(() => {
    const baseRows = rows || [];
    const selectedHealth = Number(selectedTopic?.health_score);
    const rawScores = baseRows
      .map((row) => Number(row.health_score))
      .filter((score) => Number.isFinite(score));
    const isDefaultFlatline = rawScores.length > 0 && new Set(rawScores).size === 1 && rawScores[0] === 25;

    return baseRows.map((row) => {
      if (
        selectedTopic &&
        Number.isFinite(selectedHealth) &&
        (row.health_score == null || (isDefaultFlatline && selectedHealth !== 25))
      ) {
        return { ...row, health_score: selectedHealth };
      }
      return row;
    });
  }, [rows, selectedTopic]);
  const hasData = chartRows.some((row) => row.health_score != null || row.query_volume || row.content_gaps || row.helpful_rate != null);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-success" />
            Topic Health Over Time
          </CardTitle>
          <CardDescription>Health score calculated from coverage, gaps, demand, and feedback</CardDescription>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
          <Select value={selectedTopicId} onValueChange={onTopicChange}>
            <SelectTrigger className="w-full sm:w-72" data-testid="topic-health-topic-select">
              <SelectValue placeholder="All topics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All topics</SelectItem>
              {(topics || []).map((topic) => (
                <SelectItem key={topic.topic_id} value={String(topic.topic_id)}>
                  {topic.topic_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <TrendRangeTabs value={window} onChange={onWindowChange} className="w-full sm:min-w-[320px]" />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-72 w-full rounded-xl" data-testid="topic-health-trend-loading" />
        ) : !chartRows.length || !hasData ? (
          <EmptyState
            icon={HeartPulse}
            title="No topic health trend yet"
            description="Health trend data will appear once topics have query, gap, or feedback activity."
            variant="plain"
          />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid {...commonCartesianGridProps} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatTrendTick(value, window)}
                  interval={getTrendXAxisInterval(window)}
                  {...commonXAxisProps}
                />
                <YAxis {...commonYAxisProps} domain={[0, 100]} allowDecimals={false} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  labelFormatter={(value) => formatTrendTooltipLabel(value, window)}
                  formatter={(value, name) => [value ?? 'N/A', name]}
                />
                <Legend {...commonLegendProps} />
                <Line
                  type="monotone"
                  dataKey="health_score"
                  name="Health Score"
                  stroke="hsl(var(--success))"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="content_gaps"
                  name="Content Gaps"
                  stroke="hsl(var(--warning))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TrendingTopicsPage() {
  const { hasRole } = useAuth();
  const canViewTopicHealth = hasRole(['admin', 'approver', 'analyst']);
  const canManageTopics = hasRole(['admin', 'approver']);
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [priorityHealthData, setPriorityHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [priorityHealthLoading, setPriorityHealthLoading] = useState(false);
  const [queryTrendLoading, setQueryTrendLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [detailWindow, setDetailWindow] = useState('7d');
  const [queryTrendWindow, setQueryTrendWindow] = useState('7d');
  const [mapWindow, setMapWindow] = useState('7d');
  const [healthWindow, setHealthWindow] = useState('7d');
  const [priorityHealthWindow, setPriorityHealthWindow] = useState('7d');
  const [healthTopicId, setHealthTopicId] = useState('all');
  const [activeTab, setActiveTab] = useState('details');
  const [topicViewMode, setTopicViewMode] = useState('card');
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      try {
        const result = await api.get(`/analytics/trending-topics?window=${detailWindow}&tz=${BROWSER_TZ}`);
        setData(result.data);
      } catch (err) {
        console.error('Failed to fetch trending topics:', err);
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };
    fetchData();
  }, [detailWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchTrendData = async () => {
      setQueryTrendLoading(true);
      try {
        const result = await api.get(`/analytics/trending-topics?window=${queryTrendWindow}&tz=${BROWSER_TZ}`);
        if (cancelled) return;
        setTrendData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch topic trend data:', err);
      }
      setQueryTrendLoading(false);
    };
    fetchTrendData();
    return () => {
      cancelled = true;
    };
  }, [queryTrendWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      if (activeTab !== 'world') return;

      setMapLoading(true);
      try {
        const result = await api.get(`/analytics/trending-topics?window=${mapWindow}&tz=${BROWSER_TZ}`);
        if (cancelled) return;
        setMapData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch topic map data:', err);
        setMapData(null);
      }
      setMapLoading(false);
    };
    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, mapWindow]);

  useEffect(() => {
    if (!canViewTopicHealth) {
      setHealthData(null);
      setHealthLoading(false);
      return;
    }

    let cancelled = false;
    const fetchHealth = async () => {
      setHealthLoading(true);
      try {
        const topicParam = healthTopicId === 'all' ? '' : `&topic_id=${encodeURIComponent(healthTopicId)}`;
        const result = await api.get(`/analytics/topic-health?window=${healthWindow}&tz=${BROWSER_TZ}${topicParam}`);
        if (cancelled) return;
        setHealthData(result.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch topic health:', err);
        setHealthData(null);
      } finally {
        if (!cancelled) setHealthLoading(false);
      }
    };
    fetchHealth();
    return () => {
      cancelled = true;
    };
  }, [canViewTopicHealth, healthWindow, healthTopicId]);

  useEffect(() => {
    if (!canViewTopicHealth) {
      setPriorityHealthData(null);
      setPriorityHealthLoading(false);
      return;
    }

    const fetchPriorityHealth = async () => {
      setPriorityHealthLoading(true);
      try {
        const result = await api.get(`/analytics/topic-health?window=${priorityHealthWindow}&tz=${BROWSER_TZ}`);
        setPriorityHealthData(result.data);
      } catch (err) {
        console.error('Failed to fetch topic health priority data:', err);
        setPriorityHealthData(null);
      } finally {
        setPriorityHealthLoading(false);
      }
    };
    fetchPriorityHealth();
  }, [canViewTopicHealth, priorityHealthWindow]);

  const { trending, emerging } = data || {};
  const { regional_trends, country_trends } = mapData || data || {};
  const trendTopics = trendData?.trending || trending || [];
  const volumeTrend = trendData?.volume_trend || data?.volume_trend || [];
  const healthSummary = priorityHealthData?.summary || healthData?.summary || {};
  const topicSeries = (trendTopics || [])
    .slice(0, 5)
    .filter((topic) => topic.topic_id)
    .map((topic, index) => ({
      key: `topic_${topic.topic_id}`,
      id: String(topic.topic_id),
      name: topic.topic,
      color: topic.color || TOPIC_SERIES_COLORS[index % TOPIC_SERIES_COLORS.length],
    }));
  const topicVolumeTrend = (volumeTrend || []).map((bucket) => {
    const row = {
      date: bucket.date,
      volume: Number(bucket.volume || 0),
    };
    topicSeries.forEach((topic) => {
      row[topic.key] = Number(bucket.topics?.[topic.id]?.volume || 0);
    });
    return row;
  });

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        banner
        bannerPageKey="conversations-trending"
        kpis={4}
        tabs
        sections={2}
        sectionClassName="h-80"
        data-testid="trending-topics-loading"
      />
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="trending-topics-page">
      {/* Page Banner */}
      <PageBanner pageKey="conversations-trending" />
      
      <PageHeader title="Topic Intelligence" description="Monitor topic trends and content health" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Topics Tracked"
          value={healthSummary.topics_analyzed ?? (trending || []).length}
          subtitle="Active topic signals"
          icon={ListChecks}
          color="blue"
        />
        <KpiCard
          title="Emerging"
          value={(emerging || []).length}
          subtitle="New topics gaining traction"
          icon={Sparkles}
          color="purple"
        />
        <KpiCard
          title="High Priority"
          value={healthSummary.high_priority_topics || 0}
          subtitle="Need content attention"
          icon={AlertTriangle}
          color="red"
        />
        <KpiCard
          title="Avg Helpful Rate"
          value={healthSummary.avg_helpful_rate != null ? `${healthSummary.avg_helpful_rate}%` : 'N/A'}
          subtitle="Positive feedback"
          icon={ThumbsUp}
          color="info"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar
          tabs={[
            { value: 'details', label: 'Details', icon: ListChecks },
            { value: 'trends', label: 'Trends', icon: TrendingUp },
            { value: 'world', label: 'World', icon: Globe },
          ]}
        />

        <TabsContent value="details" className="space-y-6">
          <Card className="bg-card border-border mt-6">
            <CardHeader className="gap-3 pb-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:flex-1">
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-success" />
                    Top 5 Topics
                  </CardTitle>
                  <CardDescription>By conversation volume</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  {canManageTopics && (
                    <NavLink to="/kb/topics" className="text-sm text-info hover:underline">
                      Manage Topics
                    </NavLink>
                  )}
                  <ViewModeToggle value={topicViewMode} onChange={setTopicViewMode} />
                </div>
              </div>
              <DetailTableRangeTabs value={detailWindow} onChange={setDetailWindow} className="w-full lg:w-auto lg:min-w-[360px]" />
            </CardHeader>
            <CardContent className="space-y-3">
              {topicViewMode === 'card' ? (
                (trending || []).slice(0, 5).length > 0 ? (
                  (trending || []).slice(0, 5).map((topic) => (
                    <TopicRow key={topic.topic_id || topic.topic} topic={topic} rank={topic.rank} />
                  ))
                ) : (
                  <TopicTable topics={[]} />
                )
              ) : (
                <TopicTable topics={trending || []} />
              )}
            </CardContent>
          </Card>

          {emerging?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Emerging
                </CardTitle>
                <CardDescription>New topics gaining traction</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {emerging.slice(0, 6).map((topic) => (
                  <EmergingTopicCard key={topic.id || topic.topic} topic={topic} />
                ))}
              </CardContent>
            </Card>
          )}

          {canViewTopicHealth && (
            <TopicHealthDashboard
              healthData={priorityHealthData || healthData}
              loading={priorityHealthLoading && !priorityHealthData}
              window={priorityHealthWindow}
              onWindowChange={setPriorityHealthWindow}
            />
          )}
        </TabsContent>

        <TabsContent value="trends">
          <TopicHealthTrendChart
            rows={healthLoading && !healthData ? [] : (healthData?.health_trend || [])}
            window={healthWindow}
            topics={priorityHealthData?.topics || healthData?.topics || []}
            selectedTopicId={healthTopicId}
            onTopicChange={setHealthTopicId}
            onWindowChange={setHealthWindow}
            loading={healthLoading}
          />

          <Card className="bg-card border-border mt-6">
            <CardHeader className="gap-3 pb-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-info" />
                  Top Queries Over Time
                </CardTitle>
                <CardDescription>Top topics plotted as separate query series</CardDescription>
              </div>
              <TrendRangeTabs value={queryTrendWindow} onChange={setQueryTrendWindow} className="w-full sm:w-auto sm:min-w-[320px]" />
            </CardHeader>
            <CardContent>
              {queryTrendLoading ? (
                <Skeleton className="h-80 w-full rounded-xl" data-testid="trending-query-trend-loading" />
              ) : topicSeries.length === 0 || topicVolumeTrend.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title="No topic query series available"
                  description="Series will appear once classified conversations include topic assignments."
                  variant="plain"
                />
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={topicVolumeTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid {...commonCartesianGridProps} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => formatTrendTick(value, queryTrendWindow)}
                        interval={getTrendXAxisInterval(queryTrendWindow)}
                        {...commonXAxisProps}
                      />
                      <YAxis {...commonYAxisProps} allowDecimals={false} />
                      <Tooltip 
                        contentStyle={chartTooltipStyle}
                        labelFormatter={(value) => formatTrendTooltipLabel(value, queryTrendWindow)}
                        formatter={(value, name) => [value, name]}
                      />
                      <Legend {...commonLegendProps} />
                      {topicSeries.map((topic) => (
                        <Line
                          key={topic.key}
                          type="monotone"
                          dataKey={topic.key}
                          name={topic.name}
                          stroke={topic.color}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="world">
          <RegionalMapView
            title="Topic Heatmap"
            description="Distinct tracked topics represented by region and country"
            regions={(regional_trends || []).map((region) => ({
              region: region.region,
              value: getRegionalTopicCount(region),
              secondaryValue: getRegionalQueryCount(region),
            }))}
            countries={(country_trends || []).map((country) => ({
              country: country.country,
              region: country.region,
              value: getRegionalTopicCount(country),
              secondaryValue: getRegionalQueryCount(country),
            }))}
            metricLabel="Topics"
            secondaryLabel="Queries"
            colorScale="green"
            emptyMessage="Topic heat will appear once conversations are classified by region or country."
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
